import { blobticMessage } from '../../Commun/components/BlobticMessage';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Grid, Card, CardContent, Avatar, Chip,
  IconButton, TextField, MenuItem, Divider, Tooltip,
  ToggleButton, ToggleButtonGroup, Stack, Checkbox, FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Badge as TeacherIcon, Email as EmailIcon,
  Phone as PhoneIcon, AttachMoney as RateIcon, ViewModule as GridIcon,
  ViewList as TableIcon, ArrowBack as ArrowBackIcon, PhotoCamera as PhotoCameraIcon,
  AttachFile as AttachFileIcon, CheckCircle as CheckCircleIcon, Block as InactiveIcon,
  Visibility as ViewProfileIcon, Link as LinkIcon, EventBusy as EventBusyIcon,
  Close as CloseIcon, Description as DocIcon, Download as DownloadIcon
} from '@mui/icons-material';
import { Badge, Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab, CircularProgress } from '@mui/material';
import { TeacherAbsenceRequest } from '../types/academy';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import { academyStore } from '../services/academyStore';
import { Teacher, TeacherGender, TeacherRateType } from '../types/academy';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { useSystemParameters } from '../../Commun/contexts/SystemParametersContext';
import GenericPage, { useDefaultViewMode } from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import blobticService from '../../Commun/services/blobticService';

const ENTITY_KEY = 'Blobtic_Academy,academy_teacher';
const ACTION_SUFFIX = '';
const PRIMARY_KEY = 'TCH_IDF';

const DAYS_KEYS = ['days.short.sun', 'days.short.mon', 'days.short.tue', 'days.short.wed', 'days.short.thu', 'days.short.fri', 'days.short.sat'];
const SINGLE_DAYS_KEYS = ['days.single.sun', 'days.single.mon', 'days.single.tue', 'days.single.wed', 'days.single.thu', 'days.single.fri', 'days.single.sat'];

const RATE_TYPES: TeacherRateType[] = ['hourly', 'daily', 'monthly', 'percentage', 'fixed'];
const RATE_TYPE_KEYS: Record<TeacherRateType, string> = {
  'hourly':     'academy.teachers.form.rate_type.hourly',
  'daily':      'academy.teachers.form.rate_type.daily',
  'monthly':    'academy.teachers.form.rate_type.monthly',
  'percentage': 'academy.teachers.form.rate_type.percentage',
  'fixed':      'academy.teachers.form.rate_type.fixed',
};

const fsx = {
  '& .MuiInputBase-root': { fontSize: '0.85rem' },
  '& .MuiInputLabel-root': { fontSize: '0.85rem' },
};

/** Map DB row (TCH_* columns) → frontend Teacher object */
const dtoToTeacher = (dto: any): Teacher => {
  const firstName = dto.TCH_FIRST_NAME || '';
  const lastName  = dto.TCH_LAST_NAME  || '';
  const fullName  = dto.TCH_FULL_NAME || `${firstName} ${lastName}`.trim();
  return {
    id: dto.TCH_IDF,
    first_name: firstName,
    last_name: lastName,
    full_name: fullName,
    gender: dto.TCH_GENDER || undefined,
    date_of_birth: dto.TCH_DATE_OF_BIRTH
      ? new Date(dto.TCH_DATE_OF_BIRTH).toISOString().split('T')[0]
      : '',
    email: dto.TCH_EMAIL || '',
    phone: dto.TCH_PHONE || '',
    specialization: dto.TCH_SPECIALIZATION || '',
    rate_type: dto.TCH_RATE_TYPE || 'hourly',
    hourly_rate: Number(dto.TCH_HOURLY_RATE || 0),
    hire_date: dto.TCH_HIRE_DATE
      ? new Date(dto.TCH_HIRE_DATE).toISOString().split('T')[0]
      : undefined,
    is_active: dto.TCH_IS_ACTIVE != null ? Boolean(dto.TCH_IS_ACTIVE) : true,
    bio: dto.TCH_BIO || '',
    avatar_url: dto.TCH_AVATAR_URL || undefined,
    photo_base64: dto.TCH_PHOTO_BASE64 || undefined,
    photo_mime_type: dto.TCH_PHOTO_MIME_TYPE || undefined,
    cv_file_name: dto.TCH_CV_FILE_NAME || undefined,
    cv_file_base64: dto.TCH_CV_FILE_BASE64 || undefined,
    cv_file_mime_type: dto.TCH_CV_FILE_MIME_TYPE || undefined,
    dos_idf: dto.DOS_IDF ? Number(dto.DOS_IDF) : undefined,
    dos_code: dto.DOS_CODE || undefined,
    dos_lib: dto.DOS_LIB || undefined,
    user_lib: dto.USER_LIB || undefined,
    last_updt: dto.LAST_UPDT
      ? new Date(dto.LAST_UPDT).toLocaleString('fr-DZ')
      : undefined,
    portal_active: dto.TCH_PORTAL_ACTIVE != null ? Boolean(Number(dto.TCH_PORTAL_ACTIVE)) : false,
    portal_password: dto.TCH_PORTAL_PASSWORD || '',
    can_take_attendance: dto.TCH_CAN_TAKE_ATTENDANCE != null ? Boolean(Number(dto.TCH_CAN_TAKE_ATTENDANCE)) : false,
  };
};

/** Map frontend Teacher object → DB column names expected by Academy_Teacher_DM */
const teacherToDto = (t: Partial<Teacher>): Record<string, any> => {
  const firstName = t.first_name || '';
  const lastName  = t.last_name || '';
  const fullName  = t.full_name || `${firstName} ${lastName}`.trim();
  return {
    TCH_IDF: (t as any).TCH_IDF ?? t.id,
    TCH_FIRST_NAME: firstName,
    TCH_LAST_NAME: lastName,
    TCH_FULL_NAME: fullName,
    TCH_GENDER: t.gender || null,
    TCH_DATE_OF_BIRTH: t.date_of_birth || null,
    TCH_EMAIL: t.email || null,
    TCH_PHONE: t.phone || null,
    TCH_SPECIALIZATION: t.specialization || null,
    TCH_RATE_TYPE: t.rate_type || 'hourly',
    TCH_HOURLY_RATE: t.hourly_rate ?? 0,
    TCH_HIRE_DATE: t.hire_date || null,
    TCH_IS_ACTIVE: t.is_active ?? true,
    TCH_BIO: t.bio || null,
    TCH_AVATAR_URL: t.avatar_url || null,
    TCH_PHOTO_BASE64: t.photo_base64 || null,
    TCH_PHOTO_MIME_TYPE: t.photo_mime_type || null,
    TCH_CV_FILE_NAME: t.cv_file_name || null,
    TCH_CV_FILE_BASE64: t.cv_file_base64 || null,
    TCH_CV_FILE_MIME_TYPE: t.cv_file_mime_type || null,
    TCH_PORTAL_ACTIVE: t.portal_active ? 1 : 0,
    TCH_PORTAL_PASSWORD: t.portal_password || '',
    TCH_CAN_TAKE_ATTENDANCE: t.can_take_attendance ? 1 : 0,
    ...(t.dos_idf ? { DOS_IDF: t.dos_idf } : {}),
  };
};

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const emptyTeacher = (): Partial<Teacher> => ({
  first_name: '',
  last_name: '',
  full_name: '',
  gender: undefined,
  date_of_birth: '',
  email: '',
  phone: '',
  specialization: '',
  rate_type: 'hourly',
  hourly_rate: 0,
  is_active: true,
  bio: '',
  photo_base64: undefined,
  photo_mime_type: undefined,
  cv_file_name: undefined,
  cv_file_base64: undefined,
  cv_file_mime_type: undefined,
  portal_active: false,
  portal_password: '',
  can_take_attendance: false,
});

export const TeachersPage: React.FC = () => {
  const { t } = useLanguage();
  
  const { getParam } = useSystemParameters();
  const currencySymbol = getParam('Currency_Symbol', 'DZD');

  // View mode: 'list' (default) or 'form' (new/edit)
  const [view, setView] = useState<'list' | 'form'>('list');

  // Teacher Absence Requests State
  const [absenceRequests, setAbsenceRequests] = useState<TeacherAbsenceRequest[]>(() => academyStore.getTeacherAbsenceRequests());
  const [openAbsenceDialog, setOpenAbsenceDialog] = useState(false);
  const [absenceTab, setAbsenceTab] = useState<'ALL' | 'PENDING' | 'APPROVED' | 'REJECTED'>('PENDING');
  const [adminComments, setAdminComments] = useState<Record<number, string>>({});
  const [updateSessionChecked, setUpdateSessionChecked] = useState<Record<number, boolean>>({});
  const [processingTarId, setProcessingTarId] = useState<number | null>(null);
  const [previewDoc, setPreviewDoc] = useState<{ name: string; base64: string } | null>(null);

  useEffect(() => {
    academyStore.ensureTeacherAbsenceRequestsLoaded();
    setAbsenceRequests(academyStore.getTeacherAbsenceRequests());
    return academyStore.subscribe(() => {
      setAbsenceRequests(academyStore.getTeacherAbsenceRequests());
    });
  }, []);

  const pendingAbsenceCount = absenceRequests.filter(r => r.status === 'PENDING').length;

  const handleValidateAbsence = async (req: TeacherAbsenceRequest, approve: boolean) => {
    const comment = adminComments[req.id] ?? req.admin_comment ?? '';
    const updateSession = updateSessionChecked[req.id] ?? true;
    setProcessingTarId(req.id);
    try {
      const res = await academyStore.validateTeacherAbsenceRequest(
        req.id,
        approve ? 'APPROVED' : 'REJECTED',
        comment,
        { updateSession: approve && updateSession, sessionStatus: 'Cancelled' }
      );
      if (res.success) {
        blobticMessage.success(
          t('common.messages.success'),
          approve ? t('academy.teachers.absence_approved_success') : t('academy.teachers.absence_rejected_success')
        );
      } else {
        blobticMessage.error(t('common.error'), res.message || 'Erreur lors de la validation');
      }
    } catch (err: any) {
      blobticMessage.error('Erreur', err.message);
    } finally {
      setProcessingTarId(null);
    }
  };

  // Grid vs Table toggle for the list (initialized from user default preference)
  const [viewMode, setViewMode] = useDefaultViewMode();

  // Teacher list from academyStore
  const [teachers, setTeachers] = useState<Teacher[]>(() => academyStore.getTeachers());
  const [listLoading, setListLoading] = useState(false);

  // Form data
  const [formData, setFormData] = useState<Partial<Teacher>>(emptyTeacher());

  // Profile drawer
  const [profileDrawerTeacher, setProfileDrawerTeacher] = useState<Teacher | null>(null);

  // File input refs
  const photoInputRef = useRef<HTMLInputElement>(null);
  const cvInputRef    = useRef<HTMLInputElement>(null);

  // Store data for busy map timeline drawer
  const groups   = academyStore.getGroups();
  const sessions = academyStore.getSessions();
  const rooms    = academyStore.getRooms();

  // Store subscription for live updates from store
  useEffect(() => {
    academyStore.ensureTeachersLoaded();
    setTeachers(academyStore.getTeachers());
    return academyStore.subscribe(() => {
      setTeachers(academyStore.getTeachers());
    });
  }, []);

  // ── Navigation helpers ──────────────────────────────────────────────────────

  const openNew = () => {
    setFormData(emptyTeacher());
    setView('form');
  };

  const openEdit = (tch: Teacher) => {
    setFormData({ ...tch });
    setView('form');
  };

  const backToList = () => {
    setView('list');
    setFormData(emptyTeacher());
  };

  // ── Form field handler ──────────────────────────────────────────────────────

  const set = <K extends keyof Teacher>(key: K, value: Teacher[K]) =>
    setFormData(prev => ({ ...prev, [key]: value }));

  const handleNameChange = (field: 'first_name' | 'last_name' | 'full_name', value: string) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      const fn = field === 'first_name' ? value : (next.first_name || '');
      const ln = field === 'last_name'  ? value : (next.last_name  || '');
      next.full_name = field === 'full_name' ? value : `${fn} ${ln}`.trim();
      return next;
    });
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const base64 = await fileToBase64(file);
      setFormData(prev => ({
        ...prev,
        photo_base64: base64,
        photo_mime_type: file.type,
      }));
    } catch (err: any) {
      blobticMessage.error('Erreur photo', err.message);
    }
  };

  const handleCvChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const base64 = await fileToBase64(file);
      setFormData(prev => ({
        ...prev,
        cv_file_name: file.name,
        cv_file_base64: base64,
        cv_file_mime_type: file.type,
      }));
    } catch (err: any) {
      blobticMessage.error('Erreur fichier', err.message);
    }
  };

  // ── Validation before save ──────────────────────────────────────────────────

  const handleValidate = (): string | null => {
    if (!formData.first_name?.trim()) return t('academy.students.validation.first_name_required');
    if (!formData.last_name?.trim())  return t('academy.students.validation.last_name_required');
    if (!formData.gender)             return t('academy.students.validation.gender_required');
    if (!formData.date_of_birth)      return t('academy.students.validation.dob_required');
    if (!formData.email?.trim())      return t('academy.teachers.validation.email_required');
    if (!formData.phone?.trim())      return t('academy.teachers.validation.phone_required');
    if (!formData.specialization?.trim()) return t('academy.teachers.validation.specialization_required');
    if (formData.hourly_rate == null || formData.hourly_rate < 0) return t('academy.teachers.validation.hourly_rate_required');
    return null;
  };

  // GenericPage auto-updates IndexedDB → academyStore refreshes via subscription.
  const handleDataChange = () => backToList();

  const handleClear = () => backToList();


  // ── Delete (from list) ──────────────────────────────────────────────────────

  const handleDeleteFromList = async (tch: Teacher) => {
    // boubaker 23/08/2026 - confirmation via le composant générique animé
    if (!(await blobticMessage.confirm({
      title: t('common.confirm.title.delete'),
      message: t('academy.teachers.confirm_delete'),
      severity: 'warning',
      confirmText: t('common.actions.confirm'),
      cancelText: t('btn.cancel')
    }))) return;
    try {
      // boubaker 23/08/2026 - la ligne n'est retirée de la liste QUE si le serveur confirme
      const res = await academyStore.deleteTeacher(tch.id);
      if (res.success) {
        blobticMessage.success(t('common.messages.success'), t('common.messages.deleted_success'));
      } else {
        blobticMessage.error(t('common.error'), res.message || 'Erreur lors de la suppression.');
      }
    } catch (e: any) {
      blobticMessage.error('Erreur', e.message);
    }
  };

  // ── Render helper for CV attachment ────────────────────────────────────────

  const openCvFile = (tch: Partial<Teacher>) => {
    if (tch.cv_file_base64 && tch.cv_file_mime_type) {
      const raw = tch.cv_file_base64.includes(',')
        ? tch.cv_file_base64.split(',')[1]
        : tch.cv_file_base64;
      try {
        const byteChars = atob(raw);
        const byteArray = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
        const blob = new Blob([byteArray], { type: tch.cv_file_mime_type });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 15000);
      } catch {
        const dataUrl = tch.cv_file_base64.startsWith('data:')
          ? tch.cv_file_base64
          : `data:${tch.cv_file_mime_type};base64,${tch.cv_file_base64}`;
        window.open(dataUrl, '_blank');
      }
    }
  };

  // ─── LIST VIEW ──────────────────────────────────────────────────────────────

  if (view === 'list') {
    return (
      <GenericPage
        title={t('academy.teachers.title')}
        icon={<TeacherIcon />}
        footerEnabled={false}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onNew={openNew}
        newButtonLabel={t('academy.teachers.add_new')}
        additionalActions={
          <Button
            variant={pendingAbsenceCount > 0 ? "contained" : "outlined"}
            color={pendingAbsenceCount > 0 ? "warning" : "inherit"}
            startIcon={
              <Badge badgeContent={pendingAbsenceCount} color="error">
                <EventBusyIcon />
              </Badge>
            }
            onClick={() => setOpenAbsenceDialog(true)}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            {t('academy.teachers.absence_requests')}
          </Button>
        }
      >
        {listLoading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">Chargement en cours...</Typography>
          </Box>
        ) : (
          viewMode === 'grid' ? (
            /* ── GRID VIEW ── */
            <Grid container spacing={2.5}>
              {teachers.map(teacher => {
                const teacherSessions = sessions.filter(s => s.teacher_id === teacher.id);
                return (
                  <Grid item xs={12} sm={6} md={4} key={teacher.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 3,
                        borderColor: teacher.is_active ? 'divider' : 'error.light',
                        opacity: teacher.is_active ? 1 : 0.7,
                        transition: 'transform 0.2s',
                        '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' }
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Avatar
                            src={teacher.photo_base64 || teacher.avatar_url}
                            sx={{ width: 56, height: 56, bgcolor: 'primary.main', fontSize: '1.4rem' }}
                          >
                            {teacher.first_name?.[0] || teacher.full_name?.[0] || 'T'}
                          </Avatar>
                          <Box sx={{ overflow: 'hidden' }}>
                            <Typography variant="h6" fontWeight="bold" noWrap>
                              {teacher.full_name || `${teacher.first_name} ${teacher.last_name}`}
                            </Typography>
                            <Chip
                              label={teacher.specialization || t('academy.common.general')}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                            {teacher.portal_active && (
                              <Chip
                                label={t('academy.teachers.portal_badge_active')}
                                size="small"
                                color="info"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.7rem', ml: 0.5 }}
                              />
                            )}
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, my: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <EmailIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary" noWrap>{teacher.email || '—'}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PhoneIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">{teacher.phone || '—'}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <RateIcon fontSize="small" color="action" />
                            <Typography variant="body2" fontWeight="bold">
                              {currencySymbol}{teacher.hourly_rate} / {t(RATE_TYPE_KEYS[teacher.rate_type || 'hourly'])}
                            </Typography>
                          </Box>
                        </Box>

                        <Divider sx={{ my: 1.5 }} />

                        {/* Busy Map Quick Overview */}
                        <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" sx={{ mb: 1 }}>
                          {t('academy.teachers.busy_map_summary', teacherSessions.length)}
                        </Typography>

                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 2 }}>
                          {DAYS_KEYS.map((dayKey, dayIdx) => {
                            const dayBooked = teacherSessions.some(s => s.day_of_week === dayIdx);
                            return (
                              <Tooltip key={dayKey} title={`${t(dayKey)}: ${dayBooked ? t('academy.teachers.drawer.booked') : t('academy.teachers.drawer.free')}`}>
                                <Box
                                  sx={{
                                    p: 0.5,
                                    textAlign: 'center',
                                    borderRadius: 1,
                                    fontSize: '0.65rem',
                                    fontWeight: 'bold',
                                    bgcolor: dayBooked ? 'primary.main' : 'action.hover',
                                    color: dayBooked ? 'white' : 'text.secondary'
                                  }}
                                >
                                  {t(SINGLE_DAYS_KEYS[dayIdx])}
                                </Box>
                              </Tooltip>
                            );
                          })}
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Button size="small" variant="outlined" onClick={() => setProfileDrawerTeacher(teacher)}>
                            {t('academy.teachers.view_profile')}
                          </Button>
                          <Box>
                            <IconButton size="small" color="primary" onClick={() => openEdit(teacher)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDeleteFromList(teacher)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            /* ── TABLE VIEW ── */
            <GenericTable
              columns={[
                {
                  id: 'full_name',
                  label: t('academy.teachers.table.teacher'),
                  minWidth: 200,
                  format: (_val, row) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar
                        src={row.photo_base64 || row.avatar_url}
                        sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: '0.9rem' }}
                      >
                        {row.first_name?.[0] || row.full_name?.[0] || 'T'}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                          {row.full_name || `${row.first_name} ${row.last_name}`}
                        </Typography>
                        {row.gender && (
                          <Typography variant="caption" color="text.secondary">
                            {row.gender === 'male' ? t('academy.teachers.form.gender_male') : t('academy.teachers.form.gender_female')}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ),
                },
                {
                  id: 'contact',
                  label: t('academy.teachers.table.contact'),
                  minWidth: 180,
                  format: (_val, row) => (
                    <Box>
                      <Typography variant="body2" color="text.primary">{row.email || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">{row.phone || '—'}</Typography>
                    </Box>
                  ),
                },
                {
                  id: 'specialization',
                  label: t('academy.teachers.table.specialization'),
                  minWidth: 150,
                  format: (val) => (
                    <Chip
                      label={val || t('academy.common.general')}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ),
                },
                {
                  id: 'rate',
                  label: t('academy.teachers.table.rate'),
                  minWidth: 140,
                  format: (_val, row) => (
                    <Typography variant="body2" fontWeight={600}>
                      {currencySymbol}{row.hourly_rate} / {t(RATE_TYPE_KEYS[row.rate_type as TeacherRateType || 'hourly'])}
                    </Typography>
                  ),
                },
                {
                  id: 'cv_file_name',
                  label: t('academy.teachers.table.cv'),
                  minWidth: 120,
                  format: (val, row) => val ? (
                    <Chip
                      icon={<AttachFileIcon />}
                      label={val}
                      size="small"
                      color="primary"
                      onClick={() => openCvFile(row)}
                      sx={{ cursor: 'pointer' }}
                    />
                  ) : <Typography variant="caption" color="text.secondary">—</Typography>,
                },
                {
                  id: 'is_active',
                  label: t('academy.teachers.table.status'),
                  minWidth: 100,
                  format: (val) => val
                    ? <Chip label={t('academy.teachers.active_badge')} color="success" size="small" variant="outlined" icon={<CheckCircleIcon />} />
                    : <Chip label={t('academy.teachers.inactive_badge')} color="error" size="small" variant="outlined" icon={<InactiveIcon />} />,
                },
                {
                  id: 'portal_active',
                  label: t('academy.teachers.portal_section'),
                  minWidth: 120,
                  format: (val) => (
                    <Chip
                      label={val ? t('academy.teachers.portal_badge_active') : t('academy.teachers.portal_badge_inactive')}
                      size="small"
                      color={val ? 'info' : 'default'}
                      variant={val ? 'filled' : 'outlined'}
                    />
                  ),
                },
              ]}
              data={teachers.map(tch => ({
                ...tch,
                full_name: tch.full_name || `${tch.first_name} ${tch.last_name}`,
                contact: null,
                rate: null,
              }))}
              onEdit={(row) => openEdit(teachers.find(t => t.id === row.id)!)}
              onDelete={(row) => handleDeleteFromList(teachers.find(t => t.id === row.id)!)}
              searchPlaceholder={t('academy.teachers.search_placeholder')}
              maxHeight="calc(100vh - 380px)"
            />
          )
        )}

        {/* Teacher Profile & Visual Weekly Busy Map Drawer */}
        {profileDrawerTeacher && (
          <Drawer
            anchor="right"
            open={!!profileDrawerTeacher}
            onClose={() => setProfileDrawerTeacher(null)}
            PaperProps={{ sx: { width: { xs: '100%', sm: 500 }, p: 3 } }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Avatar
                src={profileDrawerTeacher.photo_base64 || profileDrawerTeacher.avatar_url}
                sx={{ width: 64, height: 64, bgcolor: 'primary.main', fontSize: '1.8rem' }}
              >
                {profileDrawerTeacher.first_name?.[0] || profileDrawerTeacher.full_name?.[0] || 'T'}
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  {profileDrawerTeacher.full_name || `${profileDrawerTeacher.first_name} ${profileDrawerTeacher.last_name}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {profileDrawerTeacher.email} • {currencySymbol}{profileDrawerTeacher.hourly_rate}/{t(RATE_TYPE_KEYS[profileDrawerTeacher.rate_type || 'hourly'])}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Typography variant="h6" fontWeight="bold" gutterBottom>{t('academy.teachers.drawer.groups')}</Typography>
            <List disablePadding sx={{ mb: 3 }}>
              {groups.filter(g => g.teacher_id === profileDrawerTeacher.id).map(grp => (
                <Paper key={grp.id} variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle2" fontWeight="bold">{grp.name}</Typography>
                    <Chip
                      label={grp.status === 'Upcoming' ? t('academy.groups.status.upcoming') : grp.status === 'Ongoing' ? t('academy.groups.status.ongoing') : t('academy.groups.status.completed')}
                      size="small"
                      color={grp.status === 'Ongoing' ? 'primary' : 'default'}
                    />
                  </Box>
                </Paper>
              ))}
            </List>

            <Typography variant="h6" fontWeight="bold" gutterBottom>{t('academy.teachers.drawer.timeline')}</Typography>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.default' }}>
              <Grid container spacing={1}>
                {DAYS_KEYS.map((dayKey, dayIdx) => {
                  const daySessions = sessions.filter(s => s.teacher_id === profileDrawerTeacher.id && s.day_of_week === dayIdx);
                  return (
                    <Grid item xs={12} key={dayKey}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 0.5 }}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ width: 40 }}>{t(dayKey)}</Typography>
                        <Box sx={{ flexGrow: 1, display: 'flex', gap: 1 }}>
                          {daySessions.length === 0 ? (
                            <Chip label={t('academy.teachers.drawer.free')} size="small" variant="outlined" sx={{ borderColor: 'success.light', color: 'success.main' }} />
                          ) : (
                            daySessions.map(s => {
                              const r = rooms.find(rm => rm.id === s.room_id);
                              return (
                                <Chip
                                  key={s.id}
                                  label={`${s.start_time}-${s.end_time} (${r?.room_number || t('academy.common.room')})`}
                                  color="primary"
                                  size="small"
                                  sx={{ fontWeight: 'bold' }}
                                />
                              );
                            })
                          )}
                        </Box>
                      </Box>
                      <Divider sx={{ my: 0.5 }} />
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>

            <Box sx={{ mt: 4, textAlign: 'right' }}>
              <Button variant="contained" onClick={() => setProfileDrawerTeacher(null)}>{t('btn.close')}</Button>
            </Box>
          </Drawer>
        )}
        {/* Absence Requests Review Modal */}
        <Dialog
          open={openAbsenceDialog}
          onClose={() => setOpenAbsenceDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <EventBusyIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">
                {t('academy.teachers.absence_requests_title')}
              </Typography>
            </Box>
            <IconButton onClick={() => setOpenAbsenceDialog(false)} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <Box sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={absenceTab}
              onChange={(_, val) => setAbsenceTab(val)}
              textColor="primary"
              indicatorColor="primary"
            >
              <Tab
                label={`${t('academy.teachers.filter_pending')} (${absenceRequests.filter(r => r.status === 'PENDING').length})`}
                value="PENDING"
              />
              <Tab
                label={`${t('academy.teachers.filter_approved')} (${absenceRequests.filter(r => r.status === 'APPROVED').length})`}
                value="APPROVED"
              />
              <Tab
                label={`${t('academy.teachers.filter_rejected')} (${absenceRequests.filter(r => r.status === 'REJECTED').length})`}
                value="REJECTED"
              />
              <Tab
                label={`${t('academy.teachers.filter_all')} (${absenceRequests.length})`}
                value="ALL"
              />
            </Tabs>
          </Box>

          <DialogContent dividers sx={{ p: 3, maxHeight: '65vh', overflowY: 'auto' }}>
            {absenceRequests.filter(r => absenceTab === 'ALL' || r.status === absenceTab).length === 0 ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <EventBusyIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
                <Typography variant="body1" color="text.secondary">
                  {t('academy.teachers.no_absence_requests')}
                </Typography>
              </Box>
            ) : (
              <Stack spacing={2.5}>
                {absenceRequests
                  .filter(r => absenceTab === 'ALL' || r.status === absenceTab)
                  .map(req => {
                    const isPending = req.status === 'PENDING';
                    const isApproved = req.status === 'APPROVED';
                    const isRejected = req.status === 'REJECTED';
                    const currentComment = adminComments[req.id] !== undefined ? adminComments[req.id] : (req.admin_comment || '');
                    const updateSsn = updateSessionChecked[req.id] !== undefined ? updateSessionChecked[req.id] : true;
                    const isProcessing = processingTarId === req.id;

                    return (
                      <Card
                        key={req.id}
                        variant="outlined"
                        sx={{
                          borderRadius: 2.5,
                          borderColor: isPending ? 'warning.main' : isApproved ? 'success.light' : 'divider',
                          bgcolor: isPending ? 'background.paper' : 'action.hover'
                        }}
                      >
                        <CardContent sx={{ p: 2.5 }}>
                          {/* Card Header: Teacher info + Status Chip */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                                {req.teacher_name ? req.teacher_name[0] : 'E'}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle1" fontWeight="bold">
                                  {req.teacher_name || `Enseignant #${req.tch_idf}`}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {t('academy.teachers.request_date')} : {req.request_date || '-'}
                                </Typography>
                              </Box>
                            </Box>
                            <Chip
                              label={
                                isPending ? t('academy.teachers.filter_pending') :
                                isApproved ? t('academy.teachers.filter_approved') :
                                t('academy.teachers.filter_rejected')
                              }
                              color={isPending ? 'warning' : isApproved ? 'success' : 'error'}
                              size="small"
                              sx={{ fontWeight: 'bold' }}
                            />
                          </Box>

                          {/* Session & Date Details */}
                          {req.start_date && (
                            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                              <strong>Séance concernée :</strong> {req.start_date} {req.end_date && req.end_date !== req.start_date ? `- ${req.end_date}` : ''}
                            </Typography>
                          )}

                          {/* Teacher Reason */}
                          <Box sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 2, mb: 2, border: 1, borderColor: 'divider' }}>
                            <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block">
                              {t('academy.teachers.absence_reason')} :
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                              {req.reason || '-'}
                            </Typography>
                          </Box>

                          {/* Attached Document Button if available */}
                          {req.attachment_base64 && (
                            <Box sx={{ mb: 2 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                color="info"
                                startIcon={<DocIcon />}
                                onClick={() => setPreviewDoc({ name: req.attachment_name || 'justificatif', base64: req.attachment_base64! })}
                                sx={{ borderRadius: 1.5 }}
                              >
                                {t('academy.teachers.view_document')} ({req.attachment_name || 'Fichier'})
                              </Button>
                            </Box>
                          )}

                          <Divider sx={{ my: 1.5 }} />

                          {/* Admin Comment Section */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <TextField
                              label={t('academy.teachers.admin_comment')}
                              placeholder={t('academy.teachers.admin_comment_placeholder')}
                              multiline
                              rows={2}
                              fullWidth
                              size="small"
                              value={currentComment}
                              onChange={(e) => setAdminComments(prev => ({ ...prev, [req.id]: e.target.value }))}
                              disabled={isProcessing}
                            />

                            {isPending && (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={updateSsn}
                                      onChange={(e) => setUpdateSessionChecked(prev => ({ ...prev, [req.id]: e.target.checked }))}
                                      color="primary"
                                      size="small"
                                    />
                                  }
                                  label={
                                    <Typography variant="body2">
                                      {t('academy.teachers.update_session_prompt')}
                                    </Typography>
                                  }
                                />

                                <Stack direction="row" spacing={1}>
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    disabled={isProcessing}
                                    onClick={() => handleValidateAbsence(req, false)}
                                    sx={{ borderRadius: 1.5 }}
                                  >
                                    {t('academy.teachers.reject_absence')}
                                  </Button>
                                  <Button
                                    variant="contained"
                                    color="success"
                                    size="small"
                                    disabled={isProcessing}
                                    startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                                    onClick={() => handleValidateAbsence(req, true)}
                                    sx={{ borderRadius: 1.5, fontWeight: 'bold' }}
                                  >
                                    {t('academy.teachers.approve_absence')}
                                  </Button>
                                </Stack>
                              </Box>
                            )}

                            {!isPending && (
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="caption" color="text.secondary">
                                  Traité le : {req.decision_date ? new Date(req.decision_date).toLocaleString('fr-DZ') : '-'}
                                </Typography>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleValidateAbsence(req, isApproved)}
                                  disabled={isProcessing}
                                  sx={{ borderRadius: 1.5 }}
                                >
                                  Mettre à jour le commentaire
                                </Button>
                              </Box>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })}
              </Stack>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setOpenAbsenceDialog(false)} variant="contained">
              Fermer
            </Button>
          </DialogActions>
        </Dialog>

        {/* Document Preview Modal */}
        <Dialog
          open={!!previewDoc}
          onClose={() => setPreviewDoc(null)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, maxHeight: '85vh' } }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" fontWeight="bold">
              {previewDoc?.name}
            </Typography>
            <Box>
              {previewDoc?.base64 && (
                <IconButton
                  size="small"
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = previewDoc.base64;
                    link.download = previewDoc.name || 'justificatif';
                    link.click();
                  }}
                  sx={{ mr: 1 }}
                >
                  <DownloadIcon />
                </IconButton>
              )}
              <IconButton size="small" onClick={() => setPreviewDoc(null)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0, textAlign: 'center', bgcolor: '#00000010' }}>
            {previewDoc?.base64?.startsWith('data:application/pdf') || previewDoc?.name?.toLowerCase().endsWith('.pdf') ? (
              <iframe
                src={previewDoc.base64}
                title="Aperçu Justificatif PDF"
                style={{ width: '100%', height: '70vh', border: 'none' }}
              />
            ) : previewDoc?.base64 ? (
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                <img
                  src={previewDoc.base64}
                  alt={previewDoc.name}
                  style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 8 }}
                />
              </Box>
            ) : null}
          </DialogContent>
        </Dialog>
      </GenericPage>
    );
  }

  // ─── FORM VIEW (New / Edit) ────────────────────────────────────────────────

  const isEditing = !!(formData as any).TCH_IDF || !!formData.id;
  const formPayload = teacherToDto(formData);

  return (
    <GenericPage
      title={isEditing
        ? t('academy.teachers.form.title_edit', formData.first_name, formData.last_name)
        : t('academy.teachers.form.title_new')}
      icon={<TeacherIcon />}
      footerEnabled={true}
      footerInfo={{
        lastUpdate: formData.last_updt || '-',
        userLib: formData.user_lib || '-',
      }}
      additionalActions={
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={backToList}
        >
          {t('academy.teachers.back_to_list')}
        </Button>
      }
      crudConfig={{
        entityKey: ENTITY_KEY,
        actionSuffix: ACTION_SUFFIX,
        primaryKey: PRIMARY_KEY,
      }}
      data={formPayload}
      onDataChange={() => handleDataChange()}
      onValidate={handleValidate}
      onClear={handleClear}
    >
      <Box sx={{ maxWidth: 900, mx: 'auto', pb: 4 }}>

        {/* ── Photo + CV attachment ── */}
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap', mb: 3 }}>

          {/* Photo */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ position: 'relative' }}>
              <Avatar
                src={formData.photo_base64 || formData.avatar_url}
                sx={{
                  width: 100, height: 100,
                  bgcolor: 'primary.light',
                  fontSize: '2.2rem',
                  border: '3px solid',
                  borderColor: 'primary.main',
                }}
              >
                {!formData.photo_base64 && `${formData.first_name?.[0] ?? ''}${formData.last_name?.[0] ?? ''}`}
              </Avatar>
              <Tooltip title="Changer la photo">
                <IconButton
                  size="small"
                  onClick={() => photoInputRef.current?.click()}
                  sx={{
                    position: 'absolute', bottom: -4, right: -4,
                    bgcolor: 'primary.main', color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' },
                    width: 28, height: 28,
                  }}
                >
                  <PhotoCameraIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="caption" color="text.secondary">{t('academy.teachers.form.photo_caption')}</Typography>
            <input ref={photoInputRef} type="file" accept="image/*" hidden onChange={handlePhotoChange} />
          </Box>

          {/* CV attachment */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, justifyContent: 'center' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {t('academy.teachers.form.cv_file')}
            </Typography>
            {formData.cv_file_name ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                <Tooltip title={t('academy.teachers.form.view_cv')}>
                  <Chip
                    icon={<AttachFileIcon />}
                    label={formData.cv_file_name}
                    onClick={() => openCvFile(formData)}
                    onDelete={() => setFormData(prev => ({
                      ...prev,
                      cv_file_name: undefined,
                      cv_file_base64: undefined,
                      cv_file_mime_type: undefined,
                    }))}
                    variant="outlined"
                    color="primary"
                    sx={{ maxWidth: 280, cursor: 'pointer', '&:hover': { bgcolor: 'primary.50' } }}
                  />
                </Tooltip>
                <Button
                  size="small"
                  variant="text"
                  startIcon={<AttachFileIcon sx={{ fontSize: 14 }} />}
                  onClick={() => cvInputRef.current?.click()}
                  sx={{ textTransform: 'none', fontSize: '0.72rem', color: 'text.secondary', py: 0, px: 0.5 }}
                >
                  {t('academy.teachers.form.attach_cv')}
                </Button>
              </Box>
            ) : (
              <Button
                variant="outlined"
                size="small"
                startIcon={<AttachFileIcon />}
                onClick={() => cvInputRef.current?.click()}
                sx={{ textTransform: 'none', borderStyle: 'dashed', alignSelf: 'flex-start' }}
              >
                {t('academy.teachers.form.attach_cv')}
              </Button>
            )}
            <input
              ref={cvInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              hidden
              onChange={handleCvChange}
            />
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* ── Personal Information ── */}
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
            {t('academy.teachers.section_personal')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                required
                label={t('academy.students.form.lastname')} size="small" fullWidth sx={fsx}
                value={formData.last_name ?? ''}
                onChange={e => handleNameChange('last_name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                required
                label={t('academy.students.form.firstname')} size="small" fullWidth sx={fsx}
                value={formData.first_name ?? ''}
                onChange={e => handleNameChange('first_name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label={t('academy.teachers.modal.fullname')} size="small" fullWidth sx={fsx}
                value={formData.full_name ?? ''}
                onChange={e => handleNameChange('full_name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                select label={t('academy.teachers.form.gender')} size="small" fullWidth sx={fsx}
                value={formData.gender ?? ''}
                onChange={e => set('gender', e.target.value as TeacherGender)}
              >
                <MenuItem value="male">{t('academy.teachers.form.gender_male')}</MenuItem>
                <MenuItem value="female">{t('academy.teachers.form.gender_female')}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                label={t('academy.teachers.form.dob')} type="date" size="small" fullWidth sx={fsx}
                value={formData.date_of_birth ?? ''}
                onChange={e => set('date_of_birth', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                label={t('academy.teachers.form.email')} type="email" size="small" fullWidth sx={fsx}
                value={formData.email ?? ''}
                onChange={e => set('email', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                label={t('academy.teachers.form.phone')} size="small" fullWidth sx={fsx}
                value={formData.phone ?? ''}
                onChange={e => set('phone', e.target.value)}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* ── Professional Information ── */}
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
            {t('academy.teachers.section_professional')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                required
                label={t('academy.teachers.form.specialization')} size="small" fullWidth sx={fsx}
                value={formData.specialization ?? ''}
                onChange={e => set('specialization', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select label={t('academy.teachers.form.rate_type')} size="small" fullWidth sx={fsx}
                value={formData.rate_type ?? 'hourly'}
                onChange={e => set('rate_type', e.target.value as TeacherRateType)}
              >
                {RATE_TYPES.map(rt => (
                  <MenuItem key={rt} value={rt}>{t(RATE_TYPE_KEYS[rt])}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                label={t('academy.teachers.form.rate_amount')} type="number" size="small" fullWidth sx={fsx}
                value={formData.hourly_rate ?? 0}
                onChange={e => set('hourly_rate', Number(e.target.value))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('academy.teachers.form.hire_date')} type="date" size="small" fullWidth sx={fsx}
                value={formData.hire_date ?? ''}
                onChange={e => set('hire_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select label={t('academy.teachers.form.status')} size="small" fullWidth sx={fsx}
                value={formData.is_active ? 'active' : 'inactive'}
                onChange={e => set('is_active', e.target.value === 'active')}
              >
                <MenuItem value="active">{t('academy.teachers.active_badge')}</MenuItem>
                <MenuItem value="inactive">{t('academy.teachers.inactive_badge')}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t('academy.teachers.form.bio')} size="small" fullWidth multiline rows={3} sx={fsx}
                value={formData.bio ?? ''}
                onChange={e => set('bio', e.target.value)}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* ── Portal Access Section ── */}
        <Paper
          variant="outlined"
          sx={{
            p: 2.5,
            borderRadius: 2,
            mb: 3,
            borderColor: formData.portal_active ? 'secondary.main' : 'divider',
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(139, 92, 246, 0.04)' : 'rgba(139, 92, 246, 0.02)',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="subtitle2" fontWeight={700} color="secondary.main" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
              {t('academy.teachers.portal_section')}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              startIcon={<LinkIcon />}
              onClick={() => {
                const url = `${window.location.origin}/portal/teacher`;
                navigator.clipboard.writeText(url);
                blobticMessage.success(t('academy.teachers.portal_link_copied'));
              }}
              sx={{ textTransform: 'none', borderRadius: 1.5 }}
            >
              {t('academy.teachers.portal_copy_link')}
            </Button>
          </Box>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(formData.portal_active)}
                    onChange={(e) => set('portal_active', e.target.checked)}
                    color="secondary"
                  />
                }
                label={
                  <Typography variant="body2" fontWeight={600}>
                    {t('academy.teachers.portal_active')}
                  </Typography>
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(formData.can_take_attendance)}
                    onChange={(e) => set('can_take_attendance', e.target.checked)}
                    color="primary"
                    disabled={!formData.portal_active}
                  />
                }
                label={
                  <Typography variant="body2" fontWeight={600}>
                    {t('academy.teachers.portal_can_attendance')}
                  </Typography>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  label={t('academy.teachers.portal_password')}
                  size="small"
                  type="text"
                  fullWidth
                  disabled={!formData.portal_active}
                  value={formData.portal_password ?? ''}
                  onChange={(e) => set('portal_password', e.target.value)}
                  placeholder="Ex: Prof2026*"
                  helperText={t('academy.teachers.portal_login_hint')}
                  sx={fsx}
                />
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!formData.portal_active}
                  onClick={() => {
                    const rnd = 'Tch_' + Math.random().toString(36).slice(-6) + '!';
                    set('portal_password', rnd);
                  }}
                  sx={{ whiteSpace: 'nowrap', minWidth: 90, height: 40, mt: -2.5, textTransform: 'none' }}
                >
                  {t('academy.teachers.portal_generate_pwd')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* ── Dossier Information ── */}
        {(formData.dos_code || formData.dos_lib) && (
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
              {t('academy.teachers.section_dossier')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('academy.teachers.form.dossier_code')}
                  size="small" fullWidth disabled sx={fsx}
                  value={formData.dos_code ?? ''}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('academy.teachers.form.dossier_lib')}
                  size="small" fullWidth disabled sx={fsx}
                  value={formData.dos_lib ?? ''}
                />
              </Grid>
            </Grid>
          </Paper>
        )}

      </Box>
    </GenericPage>
  );
};

export default TeachersPage;
